import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { StackLayout } from '@euler/app/components/StackLayout';
import { useServiceRecordContext } from '@euler/app/flows/record/ServiceRecordContext';
import { ChildRecordListItem } from '@euler/app/flows/record/pages/detail/components/ChildRecordListItem';
import { ChildTaskListContentLoader } from '@euler/app/flows/task/pages/detail/components/ChildTaskList';
import { Card } from '@euler/components/Card';
import { StatusColors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useFetchAsyncState } from '@euler/functions/useFetchAsyncState';
import { VehicleServiceRecord } from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { safeMarkDevFlag } from '@euler/utils';
import { memo, useCallback } from 'react';
import { StyleProp, ViewStyle } from 'react-native';

const kForceLoading = safeMarkDevFlag(false);
const kForceError = safeMarkDevFlag(false);

export const ChildRecordList = memo(
  ({ style }: { style?: StyleProp<ViewStyle> }) => {
    const { rpx } = useResponsiveContext();
    const { serviceRecord } = useServiceRecordContext();
    const appNav = useAppNavigation();
    const { serviceRecordService } = useServiceFactory();
    const [state, fetch] = useFetchAsyncState(
      async () => {
        return await serviceRecordService.search(
          {
            isMainRecord: false,
            parentOrderNo: serviceRecord.orderNo,
          },
          undefined,
          0,
          0,
        );
      },
      { status: 'loading' },
      true,
    );

    const onViewDetail = useCallback(
      (record: VehicleServiceRecord) => {
        appNav.push('ServiceRecord', {
          id: record._id,
        });
      },
      [appNav],
    );

    if (
      ((!('result' in state) || !state.result) &&
        (state.status === 'idle' || state.status === 'loading')) ||
      kForceLoading
    ) {
      return (
        <LayoutProviderView
          css={`
            margin-bottom: ${rpx(15)}px;
          `}
        >
          <StackLayout style={style} gap={rpx(15)}>
            <ChildTaskListContentLoader />
            <ChildTaskListContentLoader />
            <ChildTaskListContentLoader />
          </StackLayout>
        </LayoutProviderView>
      );
    }

    if (state.status === 'error' || kForceError) {
      return (
        <Card
          style={style}
          css={`
            margin-bottom: ${rpx(15)}px;
          `}
        >
          <EmptyView
            error={
              kForceError || !('error' in state)
                ? new Error('504 Gateway Timeout')
                : state.error
            }
            message={`
            加载子服务记录失败，请稍后重试！\n(${
              'error' in state ? state.error?.message : '504 Gateway Timeout'
            })
          `}
            onRetry={fetch}
            buttonStyle={{
              height: rpx(32),
              minWidth: 100,
              backgroundColor: StatusColors.Danger,
              borderRadius: 0,
              marginTop: 0,
            }}
            buttonTextStyle={{
              color: 'white',
              fontSize: rpx(15),
            }}
            messageStyle={{
              fontFamily: FontFamily.NotoSans.Medium,
              marginTop: 0,
            }}
          />
        </Card>
      );
    }

    return (
      <StackLayout
        gap={rpx(15)}
        style={style}
        css={`
          margin-bottom: ${rpx(15)}px;
        `}
      >
        {state.result?.map(record => (
          <ChildRecordListItem
            key={record._id}
            record={record}
            onViewDetail={onViewDetail}
          />
        ))}
      </StackLayout>
    );
  },
);
